import React from 'react';
import { ArrayHelpers, DomHelpers, Help, Paging, SystemText } from '@salesperformanceint/e4shared';
import { ratingColour } from 'helpers/metrics';
import { hasModule } from 'helpers/profile';
import { useSelector } from 'react-redux';
import { CompetencyGridUsers } from './components/users';
import { CompetencyGridBlogs } from './components/blogs';
import CompetencyOverTime from 'views/user/1-2-1s/components/competencyOverTime';
import CompetencyExtraData from '../components/competencyExtraData';

export const Board = ({ data, item, id }) => {
    const loaded = React.useRef(null);
    const [pagedData, setPagedData] = React.useState([]);
    const [page, setPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(6);
    const [width, setWidth] = React.useState(200);
    const [hasPIs, setHasPIs] = React.useState(false);
    const [desc, setDesc] = React.useState(false);
    const [sort, setSort] = React.useState('name');
    const [showOverTime, setShowOverTime] = React.useState(false);
    const [showBlog, setShowBlog] = React.useState(false);
    const [showUsers, setShowUsers] = React.useState(false);
    const [showButtons, setShowButtons] = React.useState(true);
    const [competency, setCompetency] = React.useState(null);
    const recordCount = data.length;

    const user = useSelector(state => state.user.user);

    React.useEffect(() => {
        const pageSizeGenerator = extra => {
            const rowHeight = 70;
            const extrasHeight = 70;
            const me = document.querySelector(`#${id}`);
            if (me) setWidth(me.offsetWidth - 90 - (extra ? 20 : 0));
            let result = me ? ((me.offsetHeight - extrasHeight) / rowHeight).toFixed() : 10;
            if (result < recordCount) result--;
            setShowButtons(me.offsetWidth > 250);
            return result < 0 ? 0 : result;
        };

        const dataHasSomePIs = data.some(d => ArrayHelpers.existsAndHasLength(d.extraData));
        setHasPIs(dataHasSomePIs);

        const ps = pageSizeGenerator(dataHasSomePIs);

        const multi = desc ? -1 : 1;
        const temp = [...data].sort((a, b) => (a[sort] > b[sort] ? Number(multi) : -1 * multi));
        setPagedData(ArrayHelpers.getPage(temp, page, ps));
        setPageSize(ps);
    }, [id, item.h, item.w, page, data, sort, desc]);

    React.useEffect(() => {
        data && loaded.current && DomHelpers.truncateText('ellipse', loaded.current, 0);
    }, [data, loaded.current]);

    const st = (path, ...rest) => SystemText(`Dashboards/Competency/${path}`, ...rest);
    const cellAverage = pct => (pct === 0 ? '-' : `${pct.toFixed(1)}`);

    const modalProps = {
        item,
        competency,
    };

    const sorter = x => {
        if (!showButtons) return null;
        const current = x === sort;
        return (
            <span
                className="pointer flex-center ml5 no-wrap"
                onClick={() => {
                    setDesc(current ? !desc : false);
                    setSort(x);
                }}
            >
                <span className="flex-grow" />
                {current ? (
                    <>
                        <i data-testid="button-asc" className={`fa pointer fa-caret-up ${desc ? 'text-sm' : 'text-lg'}`} />
                        <i
                            data-testid="button-desc"
                            className={`fa pointer fa-caret-down ${!desc ? 'text-sm' : 'text-lg'}`}
                        />
                    </>
                ) : (
                    <>
                        <i data-testid="button-asc" className="fa pointer fa-caret-up text-sm" />
                        <i data-testid="button-desc" className="fa pointer fa-caret-down text-sm" />
                    </>
                )}
            </span>
        );
    };

    return (
        <div ref={loaded}>
            {ArrayHelpers.existsAndHasLength(pagedData) ? (
                <table className="table table-condensed">
                    <tbody>
                        <tr>
                            <th>
                                {SystemText('Dashboard/Widgets/CompetencyGrid/Table/Headers/Competency')}
                                {sorter('name')}
                            </th>
                            <th width="1" className="text-right">
                                {sorter('average')}
                            </th>
                            {hasPIs && <th width="1"></th>}
                        </tr>
                        {pagedData.map((competency, i) => {
                            const hasCRM =
                                user.crmIntegration && competency.crm_Job_Ids && competency.crm_Job_Ids.length > 0;

                            const withLevels = competency.people?.filter(p => p.level) || [];
                            const total = withLevels.reduce((total, c) => total + c.level, 0);
                            competency.average = total / withLevels.length;

                            let integrationsList = competency?.integrationFields
                                ? competency.integrationFields.reduce(
                                      (all, cur) =>
                                          all.some(x => x === cur.integration) ? all : all.concat([cur.integration]),
                                      []
                                  )
                                : [];
                            integrationsList = hasCRM ? [user.crmIntegration].concat(integrationsList) : integrationsList;
                            const withText = integrationsList.length === 0 ? '' : st('OverlaidWith', integrationsList);

                            return (
                                <tr key={i}>
                                    <td className="no-wrap">
                                        <div className="flex flex-center">
                                            {showButtons && <i className="fa fa-star mr10" />}
                                            <div
                                                style={{
                                                    maxWidth: `${width}px`,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                }}
                                            >
                                                {competency.name}
                                            </div>
                                        </div>
                                        {showButtons && (
                                            <div className="flex-end mt10">
                                                {hasModule(user, 'blogs') && (
                                                    <button
                                                        onClick={() => {
                                                            setShowBlog(true);
                                                            setCompetency(competency);
                                                        }}
                                                        disabled={!competency.blogsPublished}
                                                        className="btn btn-xs btn-default mr3 mb5 no-wrap"
                                                        title={st('Blogs')}
                                                    >
                                                        <i className="fa fa-comments" />
                                                        <span className="ml3">{competency.blogsPublished || 0}</span>
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => {
                                                        setShowUsers(true);
                                                        setCompetency(competency);
                                                    }}
                                                    disabled={!competency.people?.length}
                                                    className="btn btn-xs btn-default mr3 mb5 no-wrap"
                                                    title={st(
                                                        `Average/Over${competency.people?.length !== 1 ? '/Plural' : ''}`,
                                                        competency.people?.length
                                                    )}
                                                >
                                                    <i className="fa fa-users" />
                                                    <span className="ml3">{competency.people?.length || 0}</span>
                                                </button>
                                                <span className="btn-badge" title={st('Average/OverTime')}>
                                                    <button
                                                        className="mr3 mb5 btn btn-xs btn-default"
                                                        disabled={!competency.average && !integrationsList.length}
                                                        title={st('Rating/OverTime')}
                                                        onClick={() => {
                                                            setShowOverTime(true);
                                                            setCompetency(competency);
                                                        }}
                                                    >
                                                        <i className="fa fa-line-chart" />
                                                    </button>
                                                    {hasModule(user, 'integrations') && integrationsList.length > 0 && (
                                                        <span title={withText} className="hidden-sm hidden-xs">
                                                            <img
                                                                className="crm-icon"
                                                                src="/dist/img/metrics-icon-ric.png"
                                                                alt=""
                                                                loading="lazy"
                                                            />
                                                        </span>
                                                    )}
                                                </span>
                                            </div>
                                        )}
                                    </td>
                                    <td style={null} className="no-wrap text-right">
                                        <label
                                            title={SystemText(
                                                'Dashboard/Widgets/CompetencyGrid/AverageOver',
                                                competency.people?.length || 0
                                            )}
                                            className={`label ${showButtons ? 'text-lg' : ''} label-${ratingColour(
                                                competency.average,
                                                'grey'
                                            )}`}
                                        >
                                            {isNaN(competency.average) ? '???' : cellAverage(competency.average)}
                                        </label>
                                    </td>
                                    {hasPIs && (
                                        <td width="1" style={{ paddingLeft: '0' }}>
                                            <CompetencyExtraData competency={competency} id={`${id}-${i}`} />
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            ) : (
                <div>
                    {SystemText(
                        `Dashboard/Widgets/CompetencyGrid/${ArrayHelpers.existsAndHasLength(data) ? 'NoRoom' : 'NoResults'}`
                    )}
                </div>
            )}
            {recordCount > pageSize && (
                <div className="text-center mt10">
                    <Paging
                        page={page}
                        narrow={item.w < 3}
                        pageSize={pageSize}
                        recordCount={recordCount}
                        goToPage={setPage}
                    />
                </div>
            )}

            {showOverTime && (
                <CompetencyOverTime
                    {...modalProps}
                    profile={{}}
                    show={showOverTime}
                    close={() => {
                        setShowOverTime(false);
                        setCompetency(null);
                    }}
                />
            )}
            {showBlog && (
                <CompetencyGridBlogs
                    {...modalProps}
                    show={showBlog}
                    close={() => {
                        setShowBlog(false);
                        setCompetency(null);
                    }}
                />
            )}
            {showUsers && (
                <CompetencyGridUsers
                    {...modalProps}
                    show={showUsers}
                    close={() => {
                        setShowUsers(false);
                        setCompetency(null);
                    }}
                />
            )}
        </div>
    );
};

export default Board;
